import type { Component, ComponentClass } from './Component';

/**
 * Entity - a unique identifier with attached components
 */
export class Entity {
  public readonly id: string;
  private components: Map<string, Component> = new Map();
  private _destroyed = false;

  constructor(id?: string) {
    this.id = id ?? crypto.randomUUID();
  }

  /**
   * Add a component to this entity
   */
  addComponent<T extends Component>(component: T): this {
    if (this._destroyed) {
      throw new Error(`Cannot add component to destroyed entity ${this.id}`);
    }
    this.components.set(component.type, component);
    return this;
  }

  /**
   * Remove a component from this entity
   */
  removeComponent<T extends Component>(componentClass: ComponentClass<T>): this {
    this.components.delete(componentClass.type);
    return this;
  }

  /**
   * Get a component by class
   */
  getComponent<T extends Component>(componentClass: ComponentClass<T>): T | undefined {
    return this.components.get(componentClass.type) as T | undefined;
  }

  /**
   * Check if entity has a component
   */
  hasComponent<T extends Component>(componentClass: ComponentClass<T>): boolean {
    return this.components.has(componentClass.type);
  }

  /**
   * Check if entity has all specified components
   */
  hasComponents(componentClasses: ComponentClass[]): boolean {
    return componentClasses.every((c) => this.components.has(c.type));
  }

  /**
   * Get all components
   */
  getAllComponents(): Component[] {
    return Array.from(this.components.values());
  }

  /**
   * Mark entity as destroyed
   */
  destroy(): void {
    this._destroyed = true;
    this.components.clear();
  }

  /**
   * Check if entity is destroyed
   */
  get destroyed(): boolean {
    return this._destroyed;
  }
}
